/**
 * The app's icon set, inline, so it costs no request and no dependency.
 *
 * Paths are from Lucide (ISC licence, lucide.dev) except Basketball,
 * ChampionBelt and the two brand marks at the bottom, which are drawn here. Every icon is a 24x24
 * outline in currentColor, so it takes the colour and size of whatever it sits
 * in; set the size in CSS on the .icon class or a more specific one.
 *
 * Icons are decorative by default (aria-hidden). Anything that needs to be
 * announced gets its words from the control around it, not from the drawing.
 */
import type { ReactNode, SVGProps } from "react";

type IconProps = Omit<SVGProps<SVGSVGElement>, "children"> & { className?: string };

function make(name: string, paths: ReactNode) {
  const Icon = ({ className, ...rest }: IconProps) => (
    <svg
      className={`icon icon--${name}${className ? ` ${className}` : ""}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {paths}
    </svg>
  );
  Icon.displayName = `Icon(${name})`;
  return Icon;
}

export const ChevronUp = make("chevron-up", <path d="m18 15-6-6-6 6" />);
export const ChevronDown = make("chevron-down", <path d="m6 9 6 6 6-6" />);
export const ChevronLeft = make("chevron-left", <path d="m15 18-6-6 6-6" />);
export const ChevronRight = make("chevron-right", <path d="m9 18 6-6-6-6" />);
export const ChevronsUpDown = make("chevrons-up-down", <><path d="m7 15 5 5 5-5" /><path d="m7 9 5-5 5 5" /></>);
export const Close = make("x", <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>);
/** Lucide "maximize": the super-view of a live game (DSPRT-602). */
export const Maximize = make("maximize", <><path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M21 8V5a2 2 0 0 0-2-2h-3" /><path d="M3 16v3a2 2 0 0 0 2 2h3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" /></>);
export const Search = make("search", <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>);
export const ArrowRight = make("arrow-right", <><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></>);
export const Check = make("check", <path d="M20 6 9 17l-5-5" />);
export const ImagePlus = make("image-plus", <>
  <path d="M16 5h6" />
  <path d="M19 2v6" />
  <path d="M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5" />
  <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
  <circle cx="9" cy="9" r="2" />
</>);
export const Repeat = make("repeat-2", <><path d="m2 9 3-3 3 3" /><path d="M13 18H7a2 2 0 0 1-2-2V6" /><path d="m22 15-3 3-3-3" /><path d="M11 6h6a2 2 0 0 1 2 2v10" /></>);
export const Share = make("share", <><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><path d="m16 6-4-4-4 4" /><path d="M12 2v13" /></>);
export const SquarePlus = make("square-plus", <><rect width="18" height="18" x="3" y="3" rx="2" /><path d="M8 12h8" /><path d="M12 8v8" /></>);
export const Plus = make("plus", <><path d="M5 12h14" /><path d="M12 5v14" /></>);
export const Link = make("link", <><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>);
export const Ellipsis = make("ellipsis", <><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" /></>);
export const Sun = make(
  "sun",
  <>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2" /><path d="M12 20v2" />
    <path d="m4.93 4.93 1.41 1.41" /><path d="m17.66 17.66 1.41 1.41" />
    <path d="M2 12h2" /><path d="M20 12h2" />
    <path d="m6.34 17.66-1.41 1.41" /><path d="m19.07 4.93-1.41 1.41" />
  </>,
);
/** The countries pages (DSPRT-526): lucide's globe, the same 24px grid as the rest. */
export const Globe = make("globe", <><circle cx="12" cy="12" r="10" /><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" /><path d="M2 12h20" /></>);
export const Moon = make("moon", <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />);
export const Rss = make("rss", <><path d="M4 11a9 9 0 0 1 9 9" /><path d="M4 4a16 16 0 0 1 16 16" /><circle cx="5" cy="19" r="1" /></>);
export const CloudOff = make("cloud-off", <><path d="m2 2 20 20" /><path d="M5.782 5.782A7 7 0 0 0 9 19h8.5a4.5 4.5 0 0 0 1.307-.193" /><path d="M21.532 16.5A4.5 4.5 0 0 0 17.5 10h-1.79A7.008 7.008 0 0 0 10 5.07" /></>);
export const Monitor = make("monitor", <><rect width="20" height="14" x="2" y="3" rx="2" /><path d="M8 21h8" /><path d="M12 17v4" /></>);

/* Accounts (DSPRT-75). */
export const ShieldCheck = make(
  "shield-check",
  <>
    <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
    <path d="m9 12 2 2 4-4" />
  </>,
);
export const UserRound = make("circle-user-round", <><path d="M18 20a6 6 0 0 0-12 0" /><circle cx="12" cy="10" r="4" /><circle cx="12" cy="12" r="10" /></>);
export const LogOut = make("log-out", <><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /></>);
/** Outline; a favourite fills it with CSS (`fill: currentColor`) rather than a second drawing. */
export const Star = make(
  "star",
  <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />,
);
export const RefreshCw = make("refresh-cw", <><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 16H3v5" /></>);
export const Download = make("download", <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="m7 10 5 5 5-5" /><path d="M12 15V3" /></>);
export const Trash = make("trash", <><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /><path d="M10 11v6" /><path d="M14 11v6" /></>);
export const Bell = make("bell", <><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></>);
export const BellRing = make("bell-ring", <><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /><path d="M4 2C2.8 3.7 2 5.7 2 8" /><path d="M22 8c0-2.3-.8-4.3-2-6" /></>);
export const BellOff = make("bell-off", <><path d="M8.7 3A6 6 0 0 1 18 8a21.3 21.3 0 0 0 .6 5" /><path d="M17 17H3s3-2 3-9a4.67 4.67 0 0 1 .3-1.7" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /><path d="m2 2 20 20" /></>);
export const Laptop = make("laptop", <path d="M20 16V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v9m16 0H4m16 0 1.28 2.55a1 1 0 0 1-.9 1.45H3.62a1 1 0 0 1-.9-1.45L4 16" />);
export const Smartphone = make("smartphone", <><rect width="14" height="20" x="5" y="2" rx="2" ry="2" /><path d="M12 18h.01" /></>);
export const Info = make("info", <><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></>);
export const TriangleAlert = make("triangle-alert", <><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" /><path d="M12 9v4" /><path d="M12 17h.01" /></>);
export const LogIn = make("log-in", <><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><path d="m10 17 5-5-5-5" /><path d="M15 12H3" /></>);
export const Play = make("play", <polygon points="6 3 20 12 6 21 6 3" />);
export const Pause = make("pause", <><rect x="14" y="4" width="4" height="16" rx="1" /><rect x="6" y="4" width="4" height="16" rx="1" /></>);
/** Two people: Compare, two players side by side (DSPRT-212) — it shared Leaders' bar chart. */
export const Swords = make("swords", <><path d="M14.5 17.5 3 6V3h3l11.5 11.5" /><path d="m13 19 6-6" /><path d="m16 16 4 4" /><path d="m19 21 2-2" /><path d="M14.5 6.5 18 3h3v3l-3.5 3.5" /><path d="m5 14 4 4" /><path d="m7 17-3 3" /><path d="m3 19 2 2" /></>);
export const Target = make("target", <><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></>);
export const Grid = make("grid", <><rect width="7" height="7" x="3" y="3" rx="1" /><rect width="7" height="7" x="14" y="3" rx="1" /><rect width="7" height="7" x="14" y="14" rx="1" /><rect width="7" height="7" x="3" y="14" rx="1" /></>);
export const Users = make("users", <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>);
/** Two pans level: Compare's mark in the menus (DSPRT-440, #181), apart from Friends' two people. */
export const Scale = make("scale", <><path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" /><path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" /><path d="M7 21h10" /><path d="M12 3v18" /><path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2" /></>);
export const UserPlus = make("user-plus", <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M19 8v6" /><path d="M22 11h-6" /></>);
export const KeyRound = make("key-round", <><path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z" /><circle cx="16.5" cy="7.5" r=".5" fill="currentColor" /></>);
export const Inbox = make("inbox", <><polyline points="22 12 16 12 14 15 10 15 8 12 2 12" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /></>);
export const Megaphone = make("megaphone", <><path d="m3 11 18-5v12L3 14v-3z" /><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" /></>);
export const Ban = make("ban", <><circle cx="12" cy="12" r="10" /><path d="m4.9 4.9 14.2 14.2" /></>);
export const Lock = make("lock", <><rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>);
export const History = make("history", <><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5" /><path d="M12 7v5l4 2" /></>);
export const Eye = make("eye", <><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" /><circle cx="12" cy="12" r="3" /></>);
export const EyeOff = make(
  "eye-off",
  <>
    <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
    <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
    <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
    <path d="m2 2 20 20" />
  </>,
);
export const ListOrdered = make(
  "list-ordered",
  <>
    <path d="M10 12h11" /><path d="M10 18h11" /><path d="M10 6h11" />
    <path d="M4 10h2" /><path d="M4 6h1v4" /><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
  </>,
);
export const BarChart = make(
  "bar-chart",
  <><path d="M3 3v16a2 2 0 0 0 2 2h16" /><path d="M18 17V9" /><path d="M13 17V5" /><path d="M8 17v-3" /></>,
);
export const ArrowLeftRight = make(
  "arrow-left-right",
  <><path d="M8 3 4 7l4 4" /><path d="M4 7h16" /><path d="m16 21 4-4-4-4" /><path d="M20 17H4" /></>,
);
export const ClipboardList = make(
  "clipboard-list",
  <>
    <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <path d="M12 11h4" /><path d="M12 16h4" /><path d="M8 11h.01" /><path d="M8 16h.01" />
  </>,
);
export const Calendar = make(
  "calendar",
  <><path d="M8 2v4" /><path d="M16 2v4" /><rect width="18" height="18" x="3" y="4" rx="2" /><path d="M3 10h18" /></>,
);
export const House = make(
  "house",
  <><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" /><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></>,
);
/* Drawn here, not from Lucide: a championship belt for the UFC side, its
   centre plate an octagon after the UFC's cage. Straps closed at the ends so
   it doesn't read as an arrow at 14px. The NBA keeps the trophy. */
export const ChampionBelt = make(
  "champion-belt",
  <>
    <path d="M1.5 9.5h4M18.5 9.5h4M1.5 14.5h4M18.5 14.5h4M1.5 9.5v5M22.5 9.5v5" />
    <path d="M9.5 5.5h5l4 4v5l-4 4h-5l-4-4v-5Z" />
    <circle cx="12" cy="12" r="1.8" />
  </>,
);
export const Trophy = make(
  "trophy",
  <>
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </>,
);
export const PenLine = make(
  "pen-line",
  <>
    <path d="M12 20h9" />
    <path d="M16.376 3.622a1 1 0 0 1 3.002 3.002L7.368 18.635a2 2 0 0 1-.855.506l-2.872.838a.5.5 0 0 1-.62-.62l.838-2.872a2 2 0 0 1 .506-.854z" />
  </>,
);
export const UserMinus = make(
  "user-minus",
  <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 11h-6" /></>,
);
export const Activity = make(
  "activity",
  <path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2" />,
);
/** Ball outline with its seams — the Scores tab. */
export const Basketball = make(
  "basketball",
  <>
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20" /><path d="M12 2v20" />
    <path d="M5 4.9a10 10 0 0 1 0 14.2" /><path d="M19 4.9a10 10 0 0 0 0 14.2" />
  </>,
);

/* ------------------------------------------------------------------ *
 * Brand marks. Filled rather than stroked, and in the logo's own
 * colours, so they are not part of the currentColor set above.
 * ------------------------------------------------------------------ */

/**
 * The StatSummit ridge and flag — the same drawing as public/favicon.svg.
 *
 * The ridge follows --fg-strong so it flips with the theme. The flag is
 * --brand-red and never the league or club accent: a logo does not change
 * colour when the page around it does.
 */
export function LogoMark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="6 4 90 76" aria-hidden="true" focusable="false">
      <polyline
        points="12,74 34,56 48,64 72,30"
        fill="none"
        stroke="var(--fg-strong)"
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line x1="72" y1="30" x2="72" y2="10" stroke="var(--brand-red)" strokeWidth="5" strokeLinecap="round" />
      <polygon points="74,8 92,17 74,26" fill="var(--brand-red)" />
    </svg>
  );
}

/**
 * The flag alone, as a marker: "this is the top value". A league leader, a
 * career high, the best season in a career table.
 *
 * Carries its own label when it is the only thing saying so; pass
 * `label={null}` where the surrounding text already does.
 */
export function SummitFlag({ label = "Top value", className }: { label?: string | null; className?: string }) {
  return (
    <svg
      className={`summitflag${className ? ` ${className}` : ""}`}
      viewBox="70 6 24 26"
      role={label ? "img" : undefined}
      aria-label={label ?? undefined}
      aria-hidden={label ? undefined : true}
      focusable="false"
    >
      <line x1="72" y1="30" x2="72" y2="10" stroke="var(--brand-red)" strokeWidth="4" strokeLinecap="round" />
      <polygon points="74,8 92,17 74,26" fill="var(--brand-red)" />
    </svg>
  );
}

/**
 * The loading indicator: the ridge draws itself left to right, the flag goes
 * up at the summit, and the pair fades to start again.
 *
 * Under prefers-reduced-motion the CSS drops the animation and this is just
 * the logo, which still reads as "the page is working" beside its label.
 */
export function RidgeLoader() {
  return (
    <svg className="ridgeload" viewBox="6 4 90 76" aria-hidden="true" focusable="false">
      <polyline
        className="ridgeload__ridge"
        points="12,74 34,56 48,64 72,30"
        pathLength={100}
        fill="none"
        strokeWidth="8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <g className="ridgeload__flag">
        <line x1="72" y1="30" x2="72" y2="10" stroke="var(--brand-red)" strokeWidth="5" strokeLinecap="round" />
        <polygon points="74,8 92,17 74,26" fill="var(--brand-red)" />
      </g>
    </svg>
  );
}
